
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppProvider } from "@/contexts/AppContext";
import { TimerProvider } from "@/contexts/TimerContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { ModalProvider } from "@/contexts/ModalContext";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { FloatingTimer } from "@/components/timer/FloatingTimer";
import { TimerModal } from "@/components/modals/TimerModal";
import { RegistrarSessaoModal } from "@/components/modals/RegistrarSessaoModal";
import { WelcomePrompt } from "@/components/onboarding/WelcomePrompt";
import { OnboardingTour } from "@/components/onboarding/OnboardingTour";
import { AchievementWatcher } from "@/components/achievements/AchievementWatcher";
import ErrorBoundary from "@/components/layout/ErrorBoundary";
import { useIdleTimeout } from "@/hooks/useIdleTimeout";
import { Loader2 } from "lucide-react";
import { useCallback, lazy, Suspense } from "react";
import LandingPage from "./pages/LandingPage";

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Disciplinas = lazy(() => import("./pages/Disciplinas"));
const DisciplinaDetail = lazy(() => import("./pages/DisciplinaDetail"));
const Historico = lazy(() => import("./pages/Historico"));
const Revisoes = lazy(() => import("./pages/Revisoes"));
const Progresso = lazy(() => import("./pages/Progresso"));
const Estatisticas = lazy(() => import("./pages/Estatisticas"));
const Perfil = lazy(() => import("./pages/Perfil"));
const PlanoEstudos = lazy(() => import("./pages/PlanoEstudos"));
const Assinatura = lazy(() => import("./pages/Assinatura"));
const Admin = lazy(() => import("./pages/Admin"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Contato = lazy(() => import("./pages/Contato"));
const Conquistas = lazy(() => import("./pages/Conquistas"));
const Simulados = lazy(() => import("./pages/Simulados"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const BlogComoFazerCronograma = lazy(() => import("./pages/BlogComoFazerCronograma"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

function IdleLogoutGuard({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();

  const handleTimeout = useCallback(async () => {
    await signOut();
    window.location.href = '/auth';
  }, [signOut]);

  useIdleTimeout(handleTimeout, !!user);

  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, requiresMfa } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || requiresMfa) {
    return <Navigate to={requiresMfa ? "/auth" : "/landing"} replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, requiresMfa } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user && !requiresMfa) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <>
      <Suspense
        fallback={
          <div className="min-h-screen bg-background flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        }
      >
        <Routes>
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/auth" element={<PublicRoute><Auth /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
          <Route path="/disciplinas" element={<ProtectedRoute><Disciplinas /></ProtectedRoute>} />
          <Route path="/disciplinas/:id" element={<ProtectedRoute><DisciplinaDetail /></ProtectedRoute>} />
          <Route path="/historico" element={<ProtectedRoute><Historico /></ProtectedRoute>} />
          <Route path="/revisoes" element={<ProtectedRoute><Revisoes /></ProtectedRoute>} />
          <Route path="/progresso" element={<ProtectedRoute><Progresso /></ProtectedRoute>} />
          <Route path="/estatisticas" element={<ProtectedRoute><Estatisticas /></ProtectedRoute>} />
          <Route path="/plano" element={<ProtectedRoute><PlanoEstudos /></ProtectedRoute>} />
          <Route path="/perfil" element={<ProtectedRoute><Perfil /></ProtectedRoute>} />
          <Route path="/assinatura" element={<ProtectedRoute><Assinatura /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/conquistas" element={<ProtectedRoute><Conquistas /></ProtectedRoute>} />
          <Route path="/simulados" element={<ProtectedRoute><Simulados /></ProtectedRoute>} />
          <Route path="/termos" element={<TermosDeUso />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/contato" element={<Contato />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/blog/como-fazer-cronograma-de-estudos" element={<BlogComoFazerCronograma />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <FloatingTimer />
      <TimerModal />
      <RegistrarSessaoModal />
      <WelcomePrompt />
      <OnboardingTour />
      <AchievementWatcher />
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <IdleLogoutGuard>
            <SubscriptionProvider>
              <AppProvider>
                <TimerProvider>
                  <ModalProvider>
                    <OnboardingProvider>
                      <TooltipProvider>
                        <Toaster />
                        <Sonner />
                        <AppRoutes />
                        
                      </TooltipProvider>
                    </OnboardingProvider>
                  </ModalProvider>
                </TimerProvider>
              </AppProvider>
            </SubscriptionProvider>
          </IdleLogoutGuard>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
